import {
  apiClient as newApiClient,
  endpoint,
  httpCommunication,
} from "typed-api";
import { z } from "zod/v4";
import { apiDefinition as clientApiDefinition } from "./client/src/apiTyping.ts";
import { preferredNetworkValues } from "./scheduledActionSchema.ts";

const videoId = "2a2n-Q-V4rc";

export const shopifyExplainerVideo = "https://youtu.be/" + videoId;

export const shopifyEmbeddedExplainerVideo = "https://www.youtube.com/embed/" +
  videoId;

export const instantdbAppId = "4633a4fd-b3f6-4d8c-b11b-a953e63c4cee";
export const apiUrl = "https://api.prompt2bot.com";
export const maxFileSizeBytes = 10 * 1024 * 1024;
export const vmInactivityTimeoutMs = 60 * 60 * 1000;

export const posthogId = "phc_wJLQRqA2q7D1Dfyt8G1h2RSrfWuoIoAIGDwVAIEHUTL";
export const anomalisaToken = "b7badaec-009d-4e95-8d08-4b8177f71309";
export const googleAnalyticsMeasurementId = "G-GMYMWTZPBL";
export const googleAdsId = "AW-17877805997";
// Google Ads conversion tracking account (from customer.conversion_tracking_setting).
// Client gtag `send_to` MUST use AW-<trackingId>/<label>, bare AW ID does nothing.
export const googleAdsConversionTrackingId = "AW-1031308281";
export const googleAdsWebsiteSaleConversion =
  "AW-1031308281/vwB-CIuM4aQYEPmH4usD";
export const googleAdsSubscribeConversion =
  "AW-1031308281/U29nCIvj6bUYEPmH4usD";
export const googleAdsSignupConversion = "AW-1031308281/9PXNCL3NrPAbEPmH4usD";
export const metaPixelId = "4515605915323207";
export const serviceUrl = "https://prompt2bot.com";
export const termsPath = "/terms";
export const privacyPath = "/privacy";
export const termsUrl = `${serviceUrl}${termsPath}`;
export const privacyPolicyUrl = `${serviceUrl}${privacyPath}`;
export const termsOfServiceUrl = termsUrl;
export const privacyUrl = privacyPolicyUrl;
export const gaClientIdProp = "ga_client_id";
export const gaSessionIdProp = "ga_session_id";
export const whatsappForBusinessWebhookPath = "whatsapp-for-business";
export const whatsappDemoWebhookPath = "whatsapp-demo";
export const whatsappDemoBusinessWebhookPath =
  "whatsapp-demo-business-integration";
export const maxDailyDistinctUsersPerDemoNumber = 10;
export const facebookMessengerWebhookPath = "facebook-messenger";
export const instagramWebhookPath = "instagram";
export const slackWebhookPath = "slack";
export const teamsWebhookPath = "teams";
export const forwardEmailWebhookPath = "forward-email";
export const forwardEmailBounceWebhookPath = "forward-email-bounce";
export const hardBounceCategories = new Set([
  "block",
  "recipient",
  "spam",
  "virus",
]);
export const agentmailWebhookPath = "agentmail";
export const resendWebhookPath = "resend";
export const gmailWebhookPath = "gmail-webhook";
export const channelGlobalId = (botId: string) => `autobotbuilder/${botId}`;
export const discordLink = "https://discord.gg/bcjxhRfARJ";
export const whatsappHebrewGroupLink =
  "https://chat.whatsapp.com/IaO9z7WoXJM0f0QOuVqHHi";
export const whatsappEnglishGroupLink =
  "https://chat.whatsapp.com/GTZNL5MgrYTL0jK2ZCimwY";
export const prompt2botHebrewWaGroupId = "120363418038349016@g.us";
export const prompt2botEnglishWaGroupId = "120363409163505017@g.us";
export const whatsappGroupLink = whatsappHebrewGroupLink;
export const usersGroupLink = whatsappHebrewGroupLink;

export const supportEmail = "support@prompt2bot.com";

export const remotePromptToolName = "remote_prompt";
export const remoteConfigToolName = "remote_config";

export const updateBotAttributesToolName = "update_bot_attributes";
export const addTelegramTokenToolName = "add_telegram_token";
export const connectSupergreenToolName = "connect_supergreen_whatsapp";
export const deleteSupergreenWhatsappLineToolName =
  "delete_supergreen_whatsapp_line";
export const transferSupergreenAccountToolName = "transfer_supergreen_account";
export const setSecretToolName = "set_secret";
export const setupWhatsappForBusinessToolName = "setup_whatsapp_for_business";
export const disableWhatsappBusinessLineToolName =
  "disable_whatsapp_business_line";
export const enableWhatsappBusinessLineToolName =
  "enable_whatsapp_business_line";
export const verifyWhatsappNumberToolName = "verify_whatsapp_number";
export const registerWhatsappNumberToolName = "register_whatsapp_number";
export const createWhatsappTemplateToolName = "create_whatsapp_template";
export const checkWhatsappTemplateStatusToolName =
  "check_whatsapp_template_status";
export const listWhatsappTemplatesToolName = "list_whatsapp_templates";
export const sendWhatsappTemplateToolName = "send_whatsapp_template";
export const whatsappTemplateDuplicateWindowMs = 120_000;
export const deleteWhatsappTemplateToolName = "delete_whatsapp_template";
export const updateWhatsappBusinessProfileToolName =
  "update_whatsapp_business_profile";
export const setSlackBotToolName = "set_slack_bot";
export const setTeamsBotToolName = "set_teams_bot";
export const whatsappGroupManagementSkillName = "whatsapp_group_management";
export const joinWhatsappGroupToolName = "join_whatsapp_group";
export const getWhatsappGroupsToolName = "get_whatsapp_groups";
export const getWhatsappGroupMembersToolName = "get_whatsapp_group_members";
export const getUserWhatsappGroupsToolName = "get_user_whatsapp_groups";
export const addUserToWhatsappGroupToolName = "add_user_to_whatsapp_group";
export const getWhatsappGroupJoinRequestsToolName =
  "get_whatsapp_group_join_requests";
export const approveWhatsappGroupJoinRequestsToolName =
  "approve_whatsapp_group_join_requests";
export const rejectWhatsappGroupJoinRequestsToolName =
  "reject_whatsapp_group_join_requests";

export const groupChatBehaviorOptions = [
  { value: "ignore", label: "Ignore all group messages" },
  { value: "mentions", label: "Respond only to @ mentions" },
  { value: "always", label: "Respond to any message" },
] as const;

export type GroupChatBehavior =
  typeof groupChatBehaviorOptions[number]["value"];

export const groupChatBehaviors: Record<
  GroupChatBehavior,
  GroupChatBehavior
> = {
  ignore: "ignore",
  mentions: "mentions",
  always: "always",
};

export const preferredProviderOptions = [
  { value: "google", label: "Google (Gemini)" },
  { value: "moonshot", label: "Moonshot (Kimi)" },
] as const;

export type PreferredProvider =
  typeof preferredProviderOptions[number]["value"];

export const createRemoteTaskEndpoint = "create-remote-task";

export const injectContextEndpoint = "inject-context";

export const injectReplyEndpoint = "inject-reply";

export const pauseInteractionEndpoint = "pause-interaction";

export const takeoverConversationEndpoint = "takeover-conversation";
export const handoffReplyEndpoint = "handoff-reply";
export const endHandoffEndpoint = "end-handoff";
export const getHandoffConversationEndpoint = "get-handoff-conversation";

export const listScheduledTasksEndpoint = "list-scheduled-tasks";

export const deleteScheduledTaskEndpoint = "delete-scheduled-task";

export const dashboardInjectContextEndpoint = "dashboard-inject-context";

export const dashboardCreateTaskEndpoint = "dashboard-create-task";

export const dashboardGetGroupsEndpoint = "dashboard-get-groups";

export const uiUpdateEndpoint = "ui-update";

export const createProgressBarEndpoint = "create-progress-bar";
export const getDebugReportEndpoint = "get-debug-report";
export const adminGetUserStatsEndpoint = "admin-get-user-stats";
export const adminSendBuilderOutreachEndpoint = "admin-send-builder-outreach";
export const adminSendUserUpdateEndpoint = "admin-send-user-update";
export const adminListUsersEndpoint = "admin-list-users";
export const adminGetOutreachTokenEndpoint = "admin-get-outreach-token";

export const vmExecCallbackEndpoint = "vm-exec-callback";

export { superAdminEmail } from "./instant.perms.ts";

export const versionEndpoint = "version";
export const getDemoNumbersOccupancyEndpoint = "get-demo-numbers-occupancy";

export const api = {
  [versionEndpoint]: endpoint({
    authRequired: false,
    input: z.object({}),
    output: z.object({
      denoVersion: z.string(),
      deploymentId: z.string(),
      modalTokenId: z.boolean(),
    }),
  }),
  [vmExecCallbackEndpoint]: endpoint({
    authRequired: false,
    input: z.object({
      secret: z.string(),
      stdout: z.string().optional(),
      stderr: z.string().optional(),
      exitCode: z.number().optional(),
      sandboxId: z.string().optional(),
      error: z.string().optional(),
      fallback: z.boolean().optional(),
    }),
    output: z.object({ ok: z.boolean() }),
  }),
  "delete-folder": endpoint({
    authRequired: true,
    input: z.object({ folderId: z.string() }),
    output: z.object({}),
  }),
  "remove-file": endpoint({
    authRequired: true,
    input: z.object({ fileId: z.string() }),
    output: z.object({}),
  }),
  "index-file": endpoint({
    authRequired: true,
    input: z.object({ fileId: z.string() }),
    output: z.object({}),
  }),
  "set-whatsapp-number": endpoint({
    authRequired: true,
    input: z.object({
      whatsappNumber: z.string(),
      botId: z.string(),
      secretToken: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "disconnect-whatsapp": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      phoneNumber: z.string().optional(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "get-whatsapp-demo-info": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      code: z.string().optional(),
      link: z.string().nullable().optional(),
      error: z.string().optional(),
    }),
  }),
  [getDemoNumbersOccupancyEndpoint]: endpoint({
    authRequired: true,
    input: z.object({}),
    output: z.object({
      occupancy: z.record(
        z.string(),
        z.object({
          current: z.number(),
          max: z.number(),
        }),
      ),
    }),
  }),
  "set-telegram-token": endpoint({
    authRequired: true,
    input: z.object({
      telegramToken: z.string(),
      botId: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "disconnect-telegram": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "set-slack-bot": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      botToken: z.string(),
      signingSecret: z.string(),
      appId: z.string().optional(),
      botUserId: z.string().optional(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "set-teams-bot": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      appId: z.string(),
      appPassword: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "setup-whatsapp-for-business": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      token: z.string(),
      appSecret: z.string(),
      wabaId: z.string(),
      phoneNumberId: z.string().optional(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
      phoneNumber: z.string().optional(),
      codeRequested: z.boolean().optional(),
      codeError: z.string().optional(),
      phoneNumbers: z.array(z.object({
        id: z.string(),
        displayNumber: z.string(),
        status: z.string(),
        error: z.string().optional(),
      })).optional(),
    }),
  }),
  "verify-whatsapp-number": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      code: z.string(),
      phoneNumberId: z.string().optional(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "request-whatsapp-code": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      phoneNumberId: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "register-whatsapp-number": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      phoneNumberId: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
      alreadyRegistered: z.boolean().optional(),
    }),
  }),
  "set-forward-email": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      apiKey: z.string(),
      fromAddress: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "set-agentmail": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      apiKey: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
      address: z.string().optional(),
    }),
  }),
  "set-resend": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      apiKey: z.string(),
      fromAddress: z.string(),
      webhookSecret: z.string().optional(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "set-secret": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      name: z.string(),
      value: z.string(),
      hosts: z.array(z.string()),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "set-variable": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      name: z.string(),
      value: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "delete-secret": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      secretId: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "delete-variable": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      variableId: z.string(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "update-variable": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      variableId: z.string(),
      name: z.string().optional(),
      value: z.string().optional(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "update-secret": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      secretId: z.string(),
      name: z.string().optional(),
      hosts: z.array(z.string()).optional(),
    }),
    output: z.object({
      success: z.boolean(),
      error: z.string().optional(),
    }),
  }),
  "validate-prompt": endpoint({
    authRequired: true,
    input: z.object({ prompt: z.string() }),
    output: z.object({
      isValid: z.boolean(),
      explanation: z.string(),
    }),
  }),
  "create-bot": endpoint({
    authRequired: true,
    input: z.object({ prompt: z.string(), name: z.string() }),
    output: z.object({
      success: z.literal(true),
      botId: z.string(),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "add-admin": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string(), email: z.string() }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "remove-admin": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string() }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  [createRemoteTaskEndpoint]: clientApiDefinition["create-remote-task"],
  [listScheduledTasksEndpoint]: clientApiDefinition["list-scheduled-tasks"],
  [deleteScheduledTaskEndpoint]: clientApiDefinition["delete-scheduled-task"],
  [injectContextEndpoint]: clientApiDefinition["inject-context"],
  [injectReplyEndpoint]: clientApiDefinition["inject-reply"],
  [pauseInteractionEndpoint]: clientApiDefinition["pause-interaction"],
  "pauseInteraction": clientApiDefinition["pause-interaction"],
  [takeoverConversationEndpoint]: clientApiDefinition["takeover-conversation"],
  [handoffReplyEndpoint]: clientApiDefinition["handoff-reply"],
  [endHandoffEndpoint]: clientApiDefinition["end-handoff"],
  [getHandoffConversationEndpoint]:
    clientApiDefinition["get-handoff-conversation"],
  [dashboardInjectContextEndpoint]: endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string().describe("The bot's external ID."),
      network: z.enum(preferredNetworkValues).describe(
        "The messaging network the user is on.",
      ),
      conversationId: z.string().describe(
        "The exact target conversation ID.",
      ),
      context: z.string().describe(
        "The context string to inject into the conversation.",
      ),
      subject: z.string().optional().describe(
        "Email subject line. Required when network is 'email'.",
      ),
    }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  [dashboardCreateTaskEndpoint]: endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      description: z.string(),
      runAt: z.string().optional().describe(
        "Local time in YYYY-MM-DDTHH:mm format. Leave empty for ASAP.",
      ),
      preferredNetwork: z.enum(preferredNetworkValues),
      targetConversationId: z.string().optional(),
      recurrenceRule: z.string().optional(),
      recurrenceEnd: z.string().optional(),
      subject: z.string().optional(),
    }),
    output: z.object({
      success: z.boolean(),
      taskId: z.string().optional(),
      error: z.string().optional(),
    }),
  }),
  [dashboardGetGroupsEndpoint]: endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      useCache: z.boolean().optional(),
    }),
    output: z.object({
      warning: z.string(),
      cacheMaxAgeSeconds: z.number(),
      groups: z.array(z.object({
        id: z.string(),
        name: z.string(),
        isAdmin: z.boolean(),
      })),
    }),
  }),
  "unsubscribe-newsletter": endpoint({
    authRequired: false,
    input: z.object({ userId: z.string(), token: z.string() }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  [getDebugReportEndpoint]: endpoint({
    authRequired: true,
    input: z.object({ id: z.string() }),
    output: z.object({
      success: z.literal(true),
      report: z.record(z.string(), z.unknown()),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  [adminGetUserStatsEndpoint]: endpoint({
    authRequired: true,
    input: z.object({ query: z.string() }),
    output: z.object({
      success: z.literal(true),
      stats: z.object({
        email: z.string().optional(),
        phone: z.string().optional(),
        telegramId: z.string().optional(),
        tier: z.string(),
        isPaid: z.boolean(),
        usedRuns: z.number(),
        limitRuns: z.number(),
        isOverQuota: z.boolean(),
        signupTimestamp: z.number().optional(),
        bots: z.array(z.object({
          id: z.string(),
          name: z.string(),
          telegramUsername: z.string().optional(),
          agentRunCount: z.number().optional(),
          llmRunCount: z.number().optional(),
          incomingMessageCount: z.number().optional(),
          promptTokenCount: z.number().optional(),
          candidatesTokenCount: z.number().optional(),
          thoughtsTokenCount: z.number().optional(),
          cachedContentTokenCount: z.number().optional(),
          imageGenerationCount: z.number().optional(),
          lastInteraction: z.number().optional(),
          supergreenWaAccounts: z.array(z.object({
            id: z.string(),
            phoneNumber: z.string(),
            isConnected: z.boolean().optional(),
          })).optional(),
        })),
        refStats: z.object({
          visitsCount: z.number(),
          signupsCount: z.number(),
          conversionsCount: z.number(),
          earnings: z.number(),
        }).optional(),
        referredBy: z.object({
          id: z.string(),
          email: z.string().optional(),
          phone: z.string().optional(),
          telegramId: z.string().optional(),
        }).optional(),
      }),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  [adminListUsersEndpoint]: endpoint({
    authRequired: true,
    input: z.object({}),
    output: z.object({
      success: z.literal(true),
      users: z.array(z.object({
        id: z.string(),
        email: z.string().optional(),
        phone: z.string().optional(),
        telegramId: z.string().optional(),
        tier: z.string(),
        isPaid: z.boolean(),
        signupTimestamp: z.number().optional(),
        lastVisit: z.number().optional(),
        agentRunCount: z.number().optional(),
        llmRunCount: z.number().optional(),
        refStats: z.object({
          visitsCount: z.number(),
          signupsCount: z.number(),
          conversionsCount: z.number(),
          earnings: z.number(),
        }),
        hasActiveRef: z.boolean(),
        botsCount: z.number(),
      })),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  [adminSendBuilderOutreachEndpoint]: endpoint({
    authRequired: true,
    input: z.object({
      phone: z.string(),
      name: z.string().optional(),
      language: z.enum(["hebrew", "english"]).optional(),
      source: z.string().optional(),
      useCase: z.string().optional(),
      context: z.string().optional(),
    }).refine(
      (data) =>
        Boolean(
          (data.useCase && data.useCase.trim()) ||
            (data.context && data.context.trim()),
        ),
      { message: "Either useCase or context must be provided." },
    ),
    output: z.object({
      success: z.literal(true),
      taskId: z.string(),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  [adminGetOutreachTokenEndpoint]: endpoint({
    authRequired: true,
    input: z.object({}),
    output: z.object({
      success: z.literal(true),
      token: z.string(),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  [adminSendUserUpdateEndpoint]: endpoint({
    authRequired: true,
    input: z.object({
      context: z.string(),
      sendToHebrewGroup: z.boolean().optional(),
      sendToEnglishGroup: z.boolean().optional(),
    }),
    output: z.object({
      success: z.literal(true),
      taskIds: z.array(z.string()),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "delete-user-memory": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string(), userId: z.string() }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "delete-all-memory": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      deleteViewChat: z.boolean().optional(),
    }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "delete-bot": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      deleteViewChat: z.boolean().optional(),
    }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "list-bot-machines": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string() }),
    output: z.object({
      success: z.boolean(),
      machines: z.array(z.object({
        id: z.string(),
        ip: z.string().optional(),
        status: z.string(),
        serverType: z.string(),
        createdAt: z.number(),
      })),
      error: z.string().optional(),
    }),
  }),
  "destroy-bot-machine": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string(), machineId: z.string() }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "install-skill": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string(), sourceUrl: z.string() }),
    output: z.object({
      success: z.boolean(),
      skillId: z.string().optional(),
      name: z.string().optional(),
      version: z.string().optional(),
      error: z.string().optional(),
    }),
  }),
  "uninstall-skill": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string(), skillId: z.string() }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "list-bot-skills": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string() }),
    output: z.object({
      success: z.boolean(),
      skills: z.array(z.object({
        id: z.string(),
        sourceUrl: z.string(),
        installedAt: z.number(),
        version: z.string().optional(),
      })),
      error: z.string().optional(),
    }),
  }),
  "search-skills": endpoint({
    authRequired: true,
    input: z.object({ query: z.string() }),
    output: z.object({
      results: z.array(z.object({
        name: z.string(),
        description: z.string(),
        version: z.string(),
      })),
      tankResults: z.array(z.object({
        name: z.string(),
        description: z.string(),
        latestVersion: z.string(),
        scanVerdict: z.string(),
        publisher: z.string(),
        downloads: z.number(),
      })).optional(),
    }),
  }),
  "set-custom-tools": clientApiDefinition["set-custom-tools"],
  "set-prompt": clientApiDefinition["set-prompt"],
  "run-agent-test": clientApiDefinition["run-agent-test"],
  "get-agent-test-result": clientApiDefinition["get-agent-test-result"],
  "set-supergreen-whatsapp": clientApiDefinition["set-supergreen-whatsapp"],
  "set-agentmail-email": clientApiDefinition["set-agentmail-email"],
  "set-resend-email": clientApiDefinition["set-resend-email"],
  "create-whatsapp-template": clientApiDefinition["create-whatsapp-template"],
  "check-whatsapp-template-status":
    clientApiDefinition["check-whatsapp-template-status"],
  "list-whatsapp-templates": clientApiDefinition["list-whatsapp-templates"],
  "delete-whatsapp-template": clientApiDefinition["delete-whatsapp-template"],
  "connect-gmail": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string() }),
    output: z.union([
      z.object({ success: z.literal(true), authUrl: z.string() }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "disconnect-gmail": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string() }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "rotate-webhook-secret": clientApiDefinition["rotate-webhook-secret"],
  "get-bot-info": clientApiDefinition["get-bot-info"],
  "create-progress-bar": clientApiDefinition["create-progress-bar"],
  [uiUpdateEndpoint]: clientApiDefinition["ui-update"],
  "stop-thread": endpoint({
    authRequired: true,
    input: z.object({ projectId: z.string() }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "request-phone-link": endpoint({
    authRequired: true,
    input: z.object({ phone: z.string() }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "verify-phone-link": endpoint({
    authRequired: true,
    input: z.object({ phone: z.string(), pin: z.string() }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "request-telegram-link": endpoint({
    authRequired: true,
    input: z.object({}),
    output: z.object({
      success: z.boolean(),
      pin: z.string().optional(),
      error: z.string().optional(),
    }),
  }),
  "disconnect-telegram-link": endpoint({
    authRequired: true,
    input: z.object({}),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "create-api-token": endpoint({
    authRequired: true,
    input: z.object({ name: z.string() }),
    output: z.object({
      success: z.literal(true),
      token: z.string(),
      tokenId: z.string(),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "list-api-tokens": endpoint({
    authRequired: true,
    input: z.object({}),
    output: z.object({
      tokens: z.array(z.object({
        id: z.string(),
        name: z.string(),
        createdAt: z.number(),
      })),
    }),
  }),
  "revoke-api-token": endpoint({
    authRequired: true,
    input: z.object({ tokenId: z.string() }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "create-bot-api-token": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string(), name: z.string() }),
    output: z.object({
      success: z.literal(true),
      token: z.string(),
      tokenId: z.string(),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "list-bot-api-tokens": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string() }),
    output: z.object({
      success: z.literal(true),
      tokens: z.array(z.object({
        id: z.string(),
        name: z.string(),
        createdAt: z.number(),
      })),
    }).or(
      z.object({
        success: z.literal(false),
        tokens: z.array(z.never()),
        error: z.string(),
      }),
    ),
  }),
  "revoke-bot-api-token": endpoint({
    authRequired: true,
    input: z.object({ botId: z.string(), tokenId: z.string() }),
    output: z.object({ success: z.boolean(), error: z.string().optional() }),
  }),
  "create-bot-api": clientApiDefinition["create-bot-api"],
  "create-skill-bot": endpoint({
    authRequired: true,
    input: z.object({
      sourceUrl: z.string(),
      name: z.string().optional(),
      prompt: z.string().optional(),
    }),
    output: z.object({
      success: z.literal(true),
      botId: z.string(),
      chatLink: z.string(),
      skillName: z.string(),
      whatsappDemoLink: z.string().nullable(),
      telegramDemoLink: z.string().optional(),
      smsDemoLink: z.string().optional(),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "track-referral-visit": endpoint({
    authRequired: false,
    input: z.object({ ref: z.string() }),
    output: z.object({ success: z.boolean() }),
  }),
  "list-agency-agents": endpoint({
    authRequired: false,
    input: z.object({}),
    output: z.object({
      repoUrl: z.string(),
      divisions: z.array(z.object({
        id: z.string(),
        label: z.string(),
        icon: z.string(),
        color: z.string(),
        agents: z.array(z.object({
          name: z.string(),
          description: z.string(),
          emoji: z.string().optional(),
          url: z.string(),
        })),
      })),
    }),
  }),
  "get-affiliate-stats": endpoint({
    authRequired: true,
    input: z.object({}),
    output: z.object({
      success: z.literal(true),
      visitsCount: z.number(),
      signupsCount: z.number(),
      conversionsCount: z.number(),
      earnings: z.number(),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "request-affiliate-payout": endpoint({
    authRequired: true,
    input: z.object({}),
    output: z.object({
      success: z.literal(true),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "lookup-skill-bot": endpoint({
    authRequired: true,
    input: z.object({ sourceUrl: z.string() }),
    output: z.object({
      success: z.literal(true),
      skillName: z.string(),
      existing: z.literal(true),
      botId: z.string(),
      chatLink: z.string(),
      whatsappDemoLink: z.string().nullable(),
      telegramDemoLink: z.string().optional(),
      smsDemoLink: z.string().optional(),
      skillPreview: z.string().optional(),
    }).or(z.object({
      success: z.literal(true),
      skillName: z.string(),
      existing: z.literal(false),
      skillPreview: z.string().optional(),
    })).or(z.object({
      success: z.literal(false),
      error: z.string(),
      suggestion: z.object({ sourceUrl: z.string(), name: z.string() })
        .optional(),
    })),
  }),
  "list-number-countries": endpoint({
    authRequired: true,
    input: z.object({}),
    output: z.object({
      success: z.literal(true),
      countries: z.array(z.object({
        countryId: z.string(),
        iso: z.string(),
        name: z.string(),
        priceUsd: z.number(),
      })),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "get-number-country-availability": endpoint({
    authRequired: true,
    input: z.object({ countryId: z.string() }),
    output: z.object({
      success: z.literal(true),
      available: z.number(),
      priceUsd: z.number(),
      sampleNumber: z.string().optional(),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "create-number-checkout-session": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      countryId: z.string().optional(),
      successUrl: z.string().optional(),
    }),
    output: z.object({
      success: z.literal(true),
      url: z.string(),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "buy-dedicated-number": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      countryId: z.string().optional(),
    }),
    output: z.object({
      success: z.literal(true),
      phoneNumber: z.string(),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "get-number-sms": endpoint({
    authRequired: true,
    input: z.object({ phoneNumber: z.string() }),
    output: z.object({
      success: z.literal(true),
      messages: z.array(z.object({
        id: z.string(),
        fromNumber: z.string(),
        body: z.string(),
        receivedAt: z.number(),
        verificationCode: z.string().optional(),
      })),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "start-whatsapp-provisioning": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      phoneNumber: z.string(),
      preferCodeLogin: z.boolean().optional(),
    }),
    output: z.object({
      success: z.literal(true),
      isConnected: z.boolean(),
      pairingCode: z.string().nullable().optional(),
      verificationCode: z.string().nullable().optional(),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "check-whatsapp-connection": endpoint({
    authRequired: true,
    input: z.object({
      botId: z.string(),
      phoneNumber: z.string(),
    }),
    output: z.object({
      success: z.literal(true),
      isConnected: z.boolean(),
      pairingCode: z.string().nullable().optional(),
      verificationCode: z.string().nullable().optional(),
    }).or(z.object({ success: z.literal(false), error: z.string() })),
  }),
  "delete-bot-api": clientApiDefinition["delete-bot-api"],
  "create-guest": clientApiDefinition["create-guest"],
  "login-with-token": clientApiDefinition["login-with-token"],
  "list-bots": clientApiDefinition["list-bots"],
} as const;

export const apiUrlSuffix = "api";

export const apiClient = newApiClient(
  httpCommunication(apiUrl + "/" + apiUrlSuffix),
  api,
);

export const botsRoute = "/bots";

export const builderBotTelegramUsername = "prompt2bot_bot";

export const builderBotWhatsappNumber = "972553005358";

export const builderRefCommand = "/ref";

export const refAttribution = (referrerId: string) => `ref:${referrerId}`;

export const builderWhatsappRefText = (referrerId: string) =>
  `${builderRefCommand} ${referrerId}`;

export const builderWhatsappRefLink = (referrerId: string) =>
  `https://wa.me/${builderBotWhatsappNumber}?text=${
    encodeURIComponent(builderWhatsappRefText(referrerId))
  }`;

export const builderBotPublicSignKey =
  "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA0vhutgobPzQ/IOX69mgJwCqF4oqt/66Z+Ww9MzO8WVrlHWY55Jp9LwQro1FHDnKAU2rTCwDVSLIbcYQ3GuwBnzugPli9c5bfLBaUv5UUEkJiuk+w2b7QO1gE4YIDtwSULbFfQOOMizF/j38sGpkdiPLjedlx/wc64ZrrOoIHwuC0iAeL4TzaZthBAEEDwO5maNKQ1/sAGXvbFeMlmL7wbopdLXoQnOPNs84G7syrK08YS25pmiUC9bQdzrhtWy/DPiPjCuCnvwWNrusRhKih9RdnPSAtR8+aEpbi5zUYj2K/tFd5VuTR2Sb1yG/jLAbu01VkisLtr9TAtsZRpQsS7QIDAQAB";

export const supergreenLanding = "https://supergreen.cc/";

export const supergreenLinesPriceId = "price_1THyI9HiG8UAKPQvMmsRaGGE";

export const botSpecificForwardEmailWebhookPath = (botId: string) =>
  [apiUrl, forwardEmailWebhookPath, botId].join("/");

export const botSpecificAgentmailWebhookPath = (botId: string) =>
  [apiUrl, agentmailWebhookPath, botId].join("/");

export const botSpecificResendWebhookPath = (botId: string) =>
  [apiUrl, resendWebhookPath, botId].join("/");

export const stripeWebhookPath = "stripe";
export const stripeCheckoutPath = "stripe-checkout";
export const stripePortalPath = "stripe-portal";
export const supergreenCapacityPath = "supergreen-capacity";

export const oauthCallbackPath = "oauth-callback";

export const freePhoneVerifiedTrialRuns = 200;
export const freeEmailTrialRuns = 100;
export const freeUnverifiedTrialRuns = 50;
export const freePostTrialRuns = 0;
export const maxSignupsPerIp = 20;

export const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
export const paymentFailureGracePeriodMs = sevenDaysMs;

export const isPaymentFailureGracePeriodExpired = (
  failedPaymentTimestamp?: number | null,
) =>
  !!failedPaymentTimestamp &&
  Date.now() - failedPaymentTimestamp >= paymentFailureGracePeriodMs;

export const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;

export const isPayingTier = (name: string | null | undefined) =>
  !!name && name !== "Free";

export const currentPeriodStart = () => {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
};

export const currentPeriodEnd = () => {
  const now = new Date();
  return Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  );
};

export const nextPeriodResetDate = () => {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1);
};

export const daysUntilReset = () => {
  const now = Date.now();
  const nextMonth = nextPeriodResetDate();
  return Math.ceil((nextMonth - now) / (1000 * 60 * 60 * 24));
};

export const computeCurrentMonthlyAnniversary = (
  startMs: number,
  nowMs: number = Date.now(),
): number => {
  const startDate = new Date(startMs);
  const targetDay = startDate.getUTCDate();
  let currentStart = new Date(startMs);
  if (currentStart.getTime() > nowMs) {
    return startMs;
  }
  while (true) {
    const year = currentStart.getUTCFullYear();
    const month = currentStart.getUTCMonth();
    const nextMonth = month + 1;
    const daysInNextMonth = new Date(
      Date.UTC(year, nextMonth + 1, 0),
    ).getUTCDate();
    const day = Math.min(targetDay, daysInNextMonth);
    const nextCandidate = new Date(
      Date.UTC(
        year,
        nextMonth,
        day,
        startDate.getUTCHours(),
        startDate.getUTCMinutes(),
        startDate.getUTCSeconds(),
        startDate.getUTCMilliseconds(),
      ),
    );
    if (nextCandidate.getTime() > nowMs) {
      return currentStart.getTime();
    }
    currentStart = nextCandidate;
  }
};

export const computeNextMonthlyAnniversary = (
  startMs: number,
  nowMs: number = Date.now(),
): number => {
  const startDate = new Date(startMs);
  const targetDay = startDate.getUTCDate();
  let candidate = new Date(startMs);
  while (candidate.getTime() <= nowMs) {
    const year = candidate.getUTCFullYear();
    const month = candidate.getUTCMonth();
    const nextMonth = month + 1;
    const daysInNextMonth = new Date(
      Date.UTC(year, nextMonth + 1, 0),
    ).getUTCDate();
    const day = Math.min(targetDay, daysInNextMonth);
    candidate = new Date(
      Date.UTC(
        year,
        nextMonth,
        day,
        startDate.getUTCHours(),
        startDate.getUTCMinutes(),
        startDate.getUTCSeconds(),
        startDate.getUTCMilliseconds(),
      ),
    );
  }
  return candidate.getTime();
};

export type UserBillingPeriodInput = {
  tier?: string | null;
  billingPeriodStart?: number | null;
};

export const isEntityInCurrentPeriod = (
  entity?: UserBillingPeriodInput | null,
  nowMs: number = Date.now(),
): boolean => {
  if (isPayingTier(entity?.tier)) {
    if (!entity?.billingPeriodStart) return false;
    return (
      entity.billingPeriodStart ===
        computeCurrentMonthlyAnniversary(entity.billingPeriodStart, nowMs)
    );
  }
  return (entity?.billingPeriodStart ?? 0) === currentPeriodStart();
};

export const userPeriodStart = (
  user?: UserBillingPeriodInput | null,
  nowMs: number = Date.now(),
): number =>
  isPayingTier(user?.tier) && user?.billingPeriodStart
    ? computeCurrentMonthlyAnniversary(user.billingPeriodStart, nowMs)
    : currentPeriodStart();

export const userPeriodResetDate = (
  user?: UserBillingPeriodInput | null,
  nowMs: number = Date.now(),
): number =>
  isPayingTier(user?.tier) && user?.billingPeriodStart
    ? computeNextMonthlyAnniversary(user.billingPeriodStart, nowMs)
    : nextPeriodResetDate();

export const userPeriodEnd = (
  user?: UserBillingPeriodInput | null,
  nowMs: number = Date.now(),
): number =>
  isPayingTier(user?.tier) && user?.billingPeriodStart
    ? userPeriodResetDate(user, nowMs) - 1
    : currentPeriodEnd();

export const userDaysUntilReset = (
  user?: UserBillingPeriodInput | null,
  nowMs: number = Date.now(),
): number =>
  Math.max(
    0,
    Math.ceil(
      (userPeriodResetDate(user, nowMs) - nowMs) / (1000 * 60 * 60 * 24),
    ),
  );

export const formatPeriodDate = (ts: number) =>
  new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

export const inFirstMonth = (
  signupTimestamp?: number | null,
) => !!signupTimestamp && Date.now() - signupTimestamp < thirtyDaysMs;

export type FreeTierUserFields = {
  phone?: string | null;
  email?: string | null;
  signupTimestamp?: number | null;
  tier?: string | null;
  failedPaymentTimestamp?: number | null;
};

export const freeTierCap = (user: FreeTierUserFields) => {
  const first = inFirstMonth(user.signupTimestamp);
  if (user.phone) return first ? freePhoneVerifiedTrialRuns : freePostTrialRuns;
  if (user.email) return first ? freeEmailTrialRuns : freePostTrialRuns;
  return first ? freeUnverifiedTrialRuns : freePostTrialRuns;
};

export const tiers = [
  {
    name: "Free",
    emoji: "\u{1F193}",
    price: 0,
    agentRunsPerMonth: freePostTrialRuns,
    freeTrialRunsPerMonth: freePhoneVerifiedTrialRuns,
    bots: null,
    stripePriceId: null,
    features: [],
  },
  {
    name: "Curious",
    emoji: "\u{1F50D}",
    price: 15,
    agentRunsPerMonth: 1500,
    freeTrialRunsPerMonth: undefined,
    bots: null,
    stripePriceId: "price_1Tb8N2HiG8UAKPQvgcgknSdK",
    features: [
      "Email support",
    ],
  },
  {
    name: "Hobby",
    emoji: "\u{1F3AF}",
    price: 30,
    agentRunsPerMonth: 4000,
    freeTrialRunsPerMonth: undefined,
    bots: null,
    stripePriceId: "price_1TLOIrHiG8UAKPQvg7nHeWSl",
    features: [],
  },
  {
    name: "Standard",
    emoji: "\u{1F31F}",
    price: 100,
    agentRunsPerMonth: 24000,
    freeTrialRunsPerMonth: undefined,
    bots: null,
    highlight: true,
    stripePriceId: "price_1TLOJ5HiG8UAKPQvCEEeSYJS",
    features: [
      "Priority support",
    ],
  },
  {
    name: "Pro",
    emoji: "\u{1F680}",
    price: 400,
    agentRunsPerMonth: 80000,
    freeTrialRunsPerMonth: undefined,
    bots: null,
    stripePriceId: "price_1TLOJLHiG8UAKPQvq0zJimkd",
    features: [
      "Dedicated support",
    ],
  },
  {
    name: "Scale",
    emoji: "\u{1F4C8}",
    price: 1000,
    agentRunsPerMonth: 500000,
    freeTrialRunsPerMonth: undefined,
    bots: null,
    stripePriceId: null,
    hideInPricingPage: true,
    features: [
      "Dedicated support",
    ],
  },
] as const;

export const imageGenerationAgentRunMultiplier = 20;

export const llmBillingEffectiveTimestamp = Date.UTC(2026, 9, 1); // 2026-10-01T00:00:00.000Z

export const isLlmBillingActive = (tierName: string = "Free") =>
  tierName === "Free" || Date.now() >= llmBillingEffectiveTimestamp;

export type Tier = (typeof tiers)[number];

export const tierByName = (name: string): Tier =>
  tiers.find((t) => t.name === name) ?? tiers[0];

type TierWithPrice = Extract<Tier, { stripePriceId: string }>;

export const upgradableTiers: readonly TierWithPrice[] = tiers.filter(
  (t): t is TierWithPrice => typeof t.stripePriceId === "string",
);

const firstUpgradableTier = upgradableTiers[0];
if (!firstUpgradableTier) throw new Error("no upgradable tiers configured");

export const upgradableTierNames: [
  TierWithPrice["name"],
  ...TierWithPrice["name"][],
] = [firstUpgradableTier.name, ...upgradableTiers.slice(1).map((t) => t.name)];

export const referralPayoutFraction = 0.2;

export const referralPayoutForTier = (name: string | null | undefined) => {
  if (!name || name === "Free") return 0;
  const tier = tiers.find((t) => t.name === name);
  if (!tier) return 0;
  return Math.round(tier.price * referralPayoutFraction);
};

export const refAttributionPrefix = "ref:";

export const refAttributionFor = (referrerId: string) =>
  `${refAttributionPrefix}${referrerId}`;

export const referrerIdFromAttribution = (
  attribution?: string | null,
) =>
  attribution?.startsWith(refAttributionPrefix)
    ? attribution.slice(refAttributionPrefix.length)
    : undefined;

export const summarizeReferrals = (
  referredUsers: { tier?: string | null }[],
  visitsCount: number,
) => {
  const conversions = referredUsers.filter((u) => isPayingTier(u.tier));
  return {
    visitsCount,
    signupsCount: referredUsers.length,
    conversionsCount: conversions.length,
    earnings: conversions.reduce(
      (sum, u) => sum + referralPayoutForTier(u.tier),
      0,
    ),
  };
};

export const resetText = "/reset";

export const debugText = "/debug";

export const defaultErrorMessage =
  `Oops something went wrong. Please try again, contact support or try ${resetText} to reset bot memory.`;
